import { useEffect, useRef, useState } from 'react';

import { Box, SystemStyleObject, Text, VStack, useSize } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';

interface UseResizeReturn {
  descriptionContainerRef: React.RefObject<HTMLDivElement>;
  showExpandButton: boolean;
  isExpanded: boolean;
  toggleExpanded: () => void;
}

/**
 * The description field is limited to 5 lines by default - this hook will detect if the text would overflow the container and
 * if so return whether to show a button to show more/less and a state updater to manage expanding and collapsing
 */
const useOverflowExpand = (): UseResizeReturn => {
  const descriptionContainerRef = useRef<HTMLDivElement>(null);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  useEffect(() => {
    const element = descriptionContainerRef.current;
    const resizeObserver = new ResizeObserver(() => {
      if (element) {
        const tolerance = 2;
        setShowExpandButton(element.scrollHeight > element.clientHeight + tolerance || isExpanded);
      }
    });

    if (element) {
      resizeObserver.observe(element);
    }

    return () => {
      if (element) {
        resizeObserver.unobserve(element);
      }
    };
  }, [isExpanded]);

  return { descriptionContainerRef, showExpandButton, isExpanded, toggleExpanded };
};

export const Description = ({
  children,
  description,
  onMomentPlay,
  numLines = 5,
}: {
  children?: React.ReactNode;
  description: string;
  /**
   * The number of lines to show before the '...show more' button appears.
   * Defaults to 5, but can be set to any number.
   */
  numLines?: number;
  onMomentPlay?: (timecode: number) => void;
}) => {
  const expanderRef = useRef<HTMLButtonElement>(null);
  const { descriptionContainerRef, showExpandButton, isExpanded, toggleExpanded } =
    useOverflowExpand();
  const containerSize = useSize(descriptionContainerRef);
  const expanderSize = useSize(expanderRef);

  const containerWidth = containerSize?.width || 0;
  const expanderWidth = expanderSize?.width || 0;
  const fadeBegin = containerWidth - expanderWidth - 24; // start our fade 24px before the '...show more' button
  const fadeEnd = containerWidth - expanderWidth - 4; // end our fade 4px before the '...show more' button
  const expanderHeight = expanderSize?.height || 22.4; // 1.4rem in pixels
  // this mask will fade out the bottom-right corner of the description text
  // we measure the height / width of the '...show more' button and use that to determine the fade area
  const gradientMask = `linear-gradient(to top, transparent 0%, transparent ${expanderHeight}px, rgb(0, 0, 0) ${expanderHeight}px, rgb(0, 0, 0) 100%), linear-gradient(to right, rgb(0, 0, 0) 0%, rgb(0, 0, 0) ${fadeBegin}px, transparent ${fadeEnd}px, transparent 100%)`;

  const oldDescriptionIdentifier = `<p class="first">`;

  const isRichTextDescription = !description.startsWith(oldDescriptionIdentifier);

  const showTimestamp = showExpandButton ? isExpanded : true;

  let customStyle: SystemStyleObject = {
    p: {
      mb: 200,
    },
  };

  if (isRichTextDescription) {
    customStyle = {
      a: {
        color: 'blue.500',
      },
      ol: {
        paddingLeft: '400',
        listStyleType: 'auto',
      },
      ul: {
        paddingLeft: '400',
        listStyleType: 'unset',
      },
      '> br:only-child': {
        display: 'none',
      },
    };
  }

  const onTimeStampClick = (e: React.MouseEvent) => {
    const time = (e.target as HTMLParagraphElement).getAttribute('data-time');
    if (time) {
      onMomentPlay?.(parseInt(time, 10));
    }
  };

  return (
    <VStack align="flex-start">
      <Box position={'relative'}>
        <Box
          onClick={onTimeStampClick}
          ref={descriptionContainerRef}
          dangerouslySetInnerHTML={{ __html: description }}
          fontSize="text-sm"
          lineHeight="text-sm"
          w="100%"
          sx={{
            maxHeight: isExpanded
              ? 'none'
              : `calc(${numLines} * var(--vimeo-fontSizes-text-sm, 1.4) * var(--vimeo-lineHeights-text-sm, 0.875rem))`,
            overflow: 'hidden',
            maskImage: showExpandButton && !isExpanded ? gradientMask : 'none',
            ...customStyle,
          }}
        />
        <Text
          as="button"
          padding="0"
          backgroundColor="transparent"
          position="absolute"
          bottom="0"
          right="0"
          color="text-secondary"
          onClick={toggleExpanded}
          ref={expanderRef}
          variant="body-md"
          visibility={showExpandButton && !isExpanded ? 'visible' : 'hidden'}
          _hover={{
            textDecoration: 'underline',
          }}
        >
          {translate('...more')}
        </Text>
      </Box>
      {showTimestamp && children}
      {isExpanded ? (
        <Text
          as="button"
          backgroundColor="transparent"
          color="text-secondary"
          variant="body-md"
          onClick={toggleExpanded}
          padding="0"
          _hover={{
            textDecoration: 'underline',
          }}
        >
          {translate('Show less')}
        </Text>
      ) : null}
    </VStack>
  );
};
